import { Stack, Typography } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  calculateProductFinalPrice,
  calculateVat,
  handleOpenSnackBar,
  roundTo2Decimal,
} from "../../../utils";
import ProductTable from "./../../../components/Tables/ProductTable";
import { useTranslation } from "react-i18next";
import AppLoader from "../../../components/AppLoader";
import GlobalButton from "../../../components/CustomButton";
import useDebounce from "../../../hooks/useDebounce";
import {
  setProductId,
  setProductPageType,
} from "../../../lib/slices/detailIdsSlice";
import {
  setProductCurrentPage,
  setProductPageLimit,
  setProductTotalCount,
  setProductTotalPages,
} from "../../../lib/slices/productPageSlice";
import {
  deleteProduct,
  exportProducts,
  getAllProducts,
  updateProductFeaturedStatus,
  updateProductMegaDiscountStatus,
  updateProductStatus,
} from "../../../services/productApiFn";
import PaperContainer from "./../../../components/Containers/PaperContainer";
import { ProductsFilters } from "./Filters";
import { deleteConfirmAlert } from "../../../components/Alert";
import { APP_ICONS } from "../../../assets";
import { CSVLink } from "react-csv";
import moment from "moment";
import {
  CURRENCY,
  DEFAULT_VAT_PERCENTAGE,
  PRODUCT_USAGE_TYPE,
} from "../../../constants/Variables";

const ProductList = ({ handleChangeTab }) => {
  const dispatch = useDispatch();
  const csvLinkRef = useRef(null);
  const { t } = useTranslation(["products", "common"]);
  const [allProducts, setAllProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportData, setExportData] = useState([]);
  const userData = useSelector((state) => state.userData.user);
  const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);
  const { currentLanguage } = useSelector((state) => state.languageReducer);
  const pagination = useSelector((state) => state.productPaginationReducer);
  const settings = useSelector((state) => state.settingsReducer.settings);

  const debouncedSearchTerm = useDebounce(
    searchKeys.productName,
    1000,
    true,
    () => {
      dispatch(setProductCurrentPage(1));
    },
  );

  // const canEditDelete =
  //   userData?.adminId === (searchKeys.vendorId || userData?.adminId);

  const headerData = [
    "table.createdAt",
    "table.store",
    "table.nameImage",
    "table.sku",
    "table.category",
    "table.subCategory",
    "table.subSubCategory",
    "table.productPrice",
    "table.profitMargin",
    "table.discount",
    "table.finalPrice",
    "table.vat",
    "table.vatApplied",
    "table.finalPriceAfterVat",
    "table.initialQuantity",
    "table.quantity",
    "table.totalOrders",
    "table.rating",
    "table.variants",
    // "table.featured",
  ];
  const tableRows = [
    "created_date",
    "store_name",
    "name_image",
    "product_sku",
    "category_name",
    "sub_category_name",
    "sub_sub_category_name",
    "product_price",
    "product_profit_margin",
    "product_discount",
    "product_final_price",
    "app_vat",
    "product_vat_amount",
    "product_final_price_after_vat",
    "product_initial_quantity",
    "product_quantity",
    "totalOrders",
    "product_rating",
    "product_variants",
    // "featured_status",
    "menu_actions",
  ];

  useEffect(() => {
    if (userData) {
      fetchchedAllProducts(
        debouncedSearchTerm,
        searchKeys.ecomCategoryId,
        searchKeys.vendorId,
        pagination.page,
        searchKeys.ecomSubCategoryId,
        searchKeys.ecomSubSubCategoryId,
        pagination.limit,
      );
    }
  }, [
    userData,
    debouncedSearchTerm,
    pagination.page,
    pagination.limit,
    searchKeys.ecomCategoryId,
    searchKeys.ecomSubCategoryId,
    searchKeys.ecomSubSubCategoryId,
    searchKeys.vendorId,
  ]);

  const fetchchedAllProducts = async (
    search,
    categoryId,
    vendorId,
    page,
    subCategoryId,
    subSubCategoryId,
    limit,
  ) => {
    let query = {
      search: search || "",
      categoryId: categoryId === "All" ? "" : categoryId || "",
      subCategoryId: subCategoryId === "All" ? "" : subCategoryId || "",
      subSubCategoryId:
        subSubCategoryId === "All" ? "" : subSubCategoryId || "",
      page: page || 1,
      limit,
      supplierId: vendorId === "all" ? "" : vendorId,
    };
    try {
      setIsLoading(true);
      const response = await getAllProducts(query);
      if (response?.data?.status === "success") {
        const products = response?.data?.data?.data;
        const pagination = response?.data?.data?.pagination;
        setAllProducts(products);
        dispatch(setProductPageLimit(pagination.limit));
        dispatch(setProductCurrentPage(pagination.page));
        dispatch(setProductTotalCount(pagination.total));
        dispatch(setProductTotalPages(pagination.totalPages));
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeProductStatus = async (e, row) => {
    try {
      setIsLoading(true);
      const response = await updateProductStatus(row?._id, { status: e });
      if (response?.data?.status === "success") {
        fetchchedAllProducts(
          debouncedSearchTerm,
          searchKeys.ecomCategoryId,
          searchKeys.vendorId,
          pagination.page,
          searchKeys.ecomSubCategoryId,
          searchKeys.ecomSubSubCategoryId,
          pagination.limit,
        );
        handleOpenSnackBar("success", response?.data?.message);
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeFeaturedStatus = async (e, row) => {
    try {
      setIsLoading(true);
      const response = await updateProductFeaturedStatus(row?._id, {
        isFeatured: e,
      });
      if (response?.data?.status === "success") {
        fetchchedAllProducts(
          debouncedSearchTerm,
          searchKeys.ecomCategoryId,
          searchKeys.vendorId,
          pagination.page,
          searchKeys.ecomSubCategoryId,
          searchKeys.ecomSubSubCategoryId,
          pagination.limit,
        );
        handleOpenSnackBar("success", response?.data?.message);
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeMegaDiscountStatus = async (e, row) => {
    try {
      setIsLoading(true);
      const response = await updateProductMegaDiscountStatus(row?._id, {
        isMegaDiscount: e,
      });
      if (response?.data?.status === "success") {
        fetchchedAllProducts(
          debouncedSearchTerm,
          searchKeys.ecomCategoryId,
          searchKeys.vendorId,
          pagination.page,
          searchKeys.ecomSubCategoryId,
          searchKeys.ecomSubSubCategoryId,
          pagination.limit,
        );
        handleOpenSnackBar("success", response?.data?.message);
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteProduct = async (row) => {
    deleteConfirmAlert({
      title: t("deleteConfirmation", { ns: "common" }),
      text: t("deleteConfirmationText", { ns: "common" }),
      confirmButtonText: t("delete", { ns: "common" }),
      cancelButtonText: t("cancel", { ns: "common" }),
    }).then(async (result) => {
      if (result) {
        try {
          setIsLoading(true);
          const response = await deleteProduct(row?._id);
          if (response?.data?.status === "success") {
            fetchchedAllProducts(
              debouncedSearchTerm,
              searchKeys.ecomCategoryId,
              searchKeys.vendorId,
              pagination.page,
              searchKeys.ecomSubCategoryId,
              searchKeys.ecomSubSubCategoryId,
              pagination.limit,
            );
            handleOpenSnackBar("success", response?.data?.message);
          } else {
            handleOpenSnackBar("error", response?.data?.message);
          }
        } catch (error) {
          handleOpenSnackBar("error", error?.message);
        } finally {
          setIsLoading(false);
        }
      }
    });
  };

  const handleEditProduct = (row) => {
    handleChangeTab("add_edit_product");
    dispatch(setProductId(row?._id));
    dispatch(setProductPageType("edit"));
  };

  // Export data for products
  const prepareExportData = async () => {
    try {
      setExportLoading(true);
      const query = {
        supplierId: searchKeys.vendorId === "all" ? "" : searchKeys.vendorId,
      };
      const response = await exportProducts(query);
      if (response?.data?.status === "success") {
        const data = response?.data?.data;
        const formattedData = [];

        data.forEach((item) => {
          if (!item.isVariant || item.variants.length === 0) {
            const finalProductPrice = calculateProductFinalPrice(
              item.price,
              item.discount,
              item.profitMargin,
            );

            // This is calculated for both the product and the base packages
            const productVat = calculateVat(
              finalProductPrice,
              settings?.tax || DEFAULT_VAT_PERCENTAGE,
            );

            // This is calculated for both the product and the base packages
            const finalPriceAfterVat = roundTo2Decimal(
              finalProductPrice + productVat,
            );

            formattedData.push({
              "Created At": moment(item.createdAt).format("MM-DD-YYYY") || "-",
              "Updated At": moment(item.updatedAt).format("MM-DD-YYYY") || "-",
              "Object Id": item._id || "-",
              "Slug In (Ro)": item.mlSlug?.ro || "-",
              "Slug In (En)": item.mlSlug?.en || "-",
              "Store Name": item.createdBy.businessName || "-",
              "Cover Image": item.coverImage || "-",
              Images: item.images.map((image) => image).join(", "),
              "Name In (Ro)": item.name?.ro || "-",
              "Name In (En)": item.name?.en || "-",
              Brand: item.brand || "-",
              "Solution Type (Ro)":
                item.solutionType
                  ?.map(
                    (type) =>
                      PRODUCT_USAGE_TYPE.find((u) => u.value === type)?.label
                        ?.ro,
                  )
                  .filter(Boolean)
                  .join(", ") || "-",
              "Solution Type (En)":
                item.solutionType
                  ?.map(
                    (type) =>
                      PRODUCT_USAGE_TYPE.find((u) => u.value === type)?.label
                        ?.en,
                  )
                  .filter(Boolean)
                  .join(", ") || "-",
              "Category (Ro)": item.category?.name?.ro || "-",
              "Category (En)": item.category?.name?.en || "-",
              "Sub Category (Ro)":
                item.subCategory?.map((sub) => sub.name?.ro).join(", ") || "-",
              "Sub Category (En)":
                item.subCategory?.map((sub) => sub.name?.en).join(", ") || "-",
              "Sub Sub Category (Ro)":
                item.subSubCategory?.map((sub) => sub.name?.ro).join(", ") ||
                "-",
              "Sub Sub Category (En)":
                item.subSubCategory?.map((sub) => sub.name?.en).join(", ") ||
                "-",
              SKU: item.sku || "-",
              Price: `${item.price} ${CURRENCY}`,
              "Profit Margin": `${item.profitMargin}%`,
              Discount: `${item.discount} ${CURRENCY}`,
              "Final Price": `${item.finalPrice} ${CURRENCY}`,
              VAT: `${settings?.tax || DEFAULT_VAT_PERCENTAGE}%`,
              "VAT Amount": `${productVat} ${CURRENCY}`,
              "Price After VAT": `${finalPriceAfterVat} ${CURRENCY}`,
              "Initial Quantity": item.initialQuantity,
              Quantity: item.quantity,
              "Variant Attributes": "-",
              Featured: item.isFeatured ? "Yes" : "No",
              Status: item.status ? "Active" : "Inactive",
              "Total Orders": item.totalOrders,
              Rating: item.rating.toFixed(0),
            });
          } else {
            item.variants.forEach((variant) => {
              const attrString = Object.entries(variant.attributes)
                .map(([key, val]) => `${key}: ${val.value}`)
                .join(" | ");

              const finalProductPrice = calculateProductFinalPrice(
                variant.price,
                variant.discount,
                variant.profitMargin,
              );

              const productVat = calculateVat(
                finalProductPrice,
                settings?.tax || DEFAULT_VAT_PERCENTAGE,
              );

              const finalPriceAfterVat = roundTo2Decimal(
                finalProductPrice + productVat,
              );

              formattedData.push({
                "Created At":
                  moment(item.createdAt).format("MM-DD-YYYY") || "-",
                "Updated At":
                  moment(item.updatedAt).format("MM-DD-YYYY") || "-",
                "Object Id": item._id || "-",
                "Slug In (Ro)": item.mlSlug?.ro || "-",
                "Slug In (En)": item.mlSlug?.en || "-",
                "Store Name": item.createdBy.businessName || "-",
                "Cover Image": variant.images[0] || "-",
                Images: variant.images.map((image) => image).join(", "),
                "Name In (Ro)": item.name?.ro || "-",
                "Name In (En)": item.name?.en || "-",
                Brand: item.brand || "-",
                "Solution Type (Ro)":
                  item.solutionType
                    ?.map(
                      (type) =>
                        PRODUCT_USAGE_TYPE.find((u) => u.value === type)?.label
                          ?.ro,
                    )
                    .filter(Boolean)
                    .join(", ") || "-",
                "Solution Type (En)":
                  item.solutionType
                    ?.map(
                      (type) =>
                        PRODUCT_USAGE_TYPE.find((u) => u.value === type)?.label
                          ?.en,
                    )
                    .filter(Boolean)
                    .join(", ") || "-",
                "Category (Ro)": item.category?.name?.ro || "-",
                "Category (En)": item.category?.name?.en || "-",
                "Sub Category (Ro)":
                  item.subCategory?.map((sub) => sub.name?.ro).join(", ") ||
                  "-",
                "Sub Category (En)":
                  item.subCategory?.map((sub) => sub.name?.en).join(", ") ||
                  "-",
                "Sub Sub Category (Ro)":
                  item.subSubCategory?.map((sub) => sub.name?.ro).join(", ") ||
                  "-",
                "Sub Sub Category (En)":
                  item.subSubCategory?.map((sub) => sub.name?.en).join(", ") ||
                  "-",
                SKU: variant.sku || "-",
                Price: `${variant.price} ${CURRENCY}`,
                "Profit Margin": `${variant.profitMargin}%`,
                Discount: `${variant.discount} ${CURRENCY}`,
                "Final Price": `${variant.finalPrice} ${CURRENCY}`,
                VAT: `${settings?.tax || DEFAULT_VAT_PERCENTAGE}%`,
                "VAT Amount": `${productVat} ${CURRENCY}`,
                "Price After VAT": `${finalPriceAfterVat} ${CURRENCY}`,
                "Initial Quantity": variant.quantity,
                Quantity: variant.quantity,
                "Variant Attributes": attrString || "-",
                Featured: item.isFeatured ? "Yes" : "No",
                Status: item.status ? "Active" : "Inactive",
                "Total Orders": item.totalOrders,
                Rating: item.rating.toFixed(0),
              });
            });
          }
        });

        setExportData(formattedData);

        // Data set hone ke baad manually click trigger karo
        setTimeout(() => {
          csvLinkRef.current?.link?.click();
        }, 100);
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setExportLoading(false);
    }
  };

  const renderHeaderAction = () => {
    return (
      <Stack
        flexGrow={1}
        direction={{ md: "row" }}
        gap={1}
        justifyContent={"flex-end"}
        alignItems={{xs: "flex-end", md: "center"}}
      >
        {/* <GlobalButton
          variant={"contained"}
          btnText={"addProduct"}
          handleClickBtn={() => {
            handleChangeTab("add_edit_product");
            dispatch(setProductId(""));
            dispatch(setProductPageType("add"));
          }}
          styleProps={{
            width: "auto",
            height: 40,
          }}
        /> */}
        <CSVLink
          ref={csvLinkRef}
          data={exportData}
          filename={"products.csv"}
          className="csv-link"
          target="_blank"
          style={{ display: "none" }}
        />

        <GlobalButton
          variant={"export_button"}
          btnText={"export"}
          startIcon={<img src={APP_ICONS.exportBlockIcon} width={20} />}
          disabled={exportLoading}
          handleClickBtn={prepareExportData}
        />
      </Stack>
    );
  };

  const handlePageChange = (event, newPage) => {
    const page = newPage + 1;
    dispatch(setProductCurrentPage(page));
  };

  const handleRowsPerPageChange = (event) => {
    const limit = parseInt(event.target.value, 10);
    dispatch(setProductPageLimit(limit));
    dispatch(setProductCurrentPage(1));
  };

  const allProductsData = useMemo(() => {
    return (
      <ProductTable
        minWidth={3000}
        tableHeader={headerData}
        displayRows={tableRows}
        tableData={allProducts}
        showEdit={true}
        showDelete={false}
        showProductStatus={true}
        showMegaDiscount={true}
        handleChangeMegaDiscountStatus={handleChangeMegaDiscountStatus}
        handleDelete={handleDeleteProduct}
        handleChangeProductStatus={handleChangeProductStatus}
        handleEdit={handleEditProduct}
        handleChangeFeaturedStatus={handleChangeFeaturedStatus}
        paginatedData={pagination}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        showActions={true}
      />
    );
  }, [allProducts]);

  return (
    <>
      <AppLoader isVisible={isLoading} />
      <PaperContainer
        title={t("productList")}
        subtitle={t("manageProducts")}
        headerActions={renderHeaderAction()}
        searchActions={<ProductsFilters />}
      >
        {allProductsData}
        {allProducts?.length === 0 && isLoading === false && (
          <Typography variant="h5" fontWeight={600} textAlign={"center"}>
            {t("noDataFound", { ns: "common" })}
          </Typography>
        )}
      </PaperContainer>
    </>
  );
};

export default ProductList;
